/**
 * MarkInvoicePaymentModal — passo 2 para UMA fatura: dizer que um pagamento já recebido a paga.
 *
 * Substitui o `InvoicePaymentModal` na tela de Títulos. A diferença estrutural não é de campo, é
 * de ato: lá o usuário CRIAVA o pagamento e liquidava a fatura na mesma submissão; aqui ele
 * ESCOLHE um pagamento que já entrou e apenas o aponta para a fatura. A liquidação depende da
 * aprovação do financeiro (passo 3).
 *
 * O que sai: data, tipo de pagamento, pagador, comprovante — tudo isso já foi informado no passo 1
 * e mostrar de novo convidaria a divergir do que está gravado.
 *
 * O que fica: o valor (quanto DESTE pagamento vai para esta fatura), as observações e as opções de
 * "fatura nova" — que aqui são uma PROMESSA, cumprida só na aprovação.
 *
 * O modo (Liquidar / Baixa Parcial / Amortizar) continua vindo do item de menu, como antes: ter
 * botão e campo permitia abrir "Liquidar" e submeter uma parcial, com o rótulo mentindo.
 */
import { useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import PendingPaymentPicker from '../../../molecules/payment/PendingPaymentPicker';
import PaymentInfoModal from './PaymentInfoModal';
import HintBadge from '../../../molecules/payment/HintBadge';
import { amountHint } from '../../../molecules/payment/paymentHints';
import {
    MarkInvoiceItem, PaymentActionPromise, PaymentRow, markInvoices,
} from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import {
    LiquidationMode, MODE_TO_TOTAL_PAYMENT, MODE_LABELS, WATERFALL_HINT,
    PAYMENT_MODES_BY_SCENARIO, clampMode,
} from '../constants/liquidationModes';

export type PaymentMode = LiquidationMode;

/** Rótulos por modo. O botão diz "Sinalizar", não "Liquidar": não é isso que a ação faz. */
const MODE_UI: Record<LiquidationMode, { heading: string; submittingLabel: string }> = {
    TOTAL: { heading: 'Sinalizar pagamento — liquidar título', submittingLabel: 'Sinalizando...' },
    PARTIAL: { heading: 'Sinalizar pagamento — baixa parcial', submittingLabel: 'Sinalizando...' },
    OPERATION_WATERFALL: { heading: 'Sinalizar pagamento — amortizar operação', submittingLabel: 'Sinalizando...' },
};

const POST_SETTLEMENT_UI = {
    heading: 'Sinalizar pagamento — pós liquidação',
    submittingLabel: 'Sinalizando...',
};

/**
 * O que fazer com a fatura anterior. Mesmas três escolhas do fluxo antigo, com o efeito DIFERIDO:
 * aqui só se registra a promessa.
 *
 * Escopo único (sem `_ALL`) porque o usuário escolheu UMA fatura no menu — aplicar no título
 * inteiro cancelaria cobrança que ninguém pediu para cancelar. Quem pede escopo de título é a
 * Identificação de Pagamento, onde a linha É o título.
 */
const PROMISE_BY_CHOICE: Record<'CANCEL' | 'UNCOLLECT' | 'KEEP', PaymentActionPromise> = {
    CANCEL: 'CANCEL',
    UNCOLLECT: 'UNCOLLECT',
    KEEP: 'NOTHING',
};

const LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white';

interface Props {
    invoice: any;
    charge: any;
    mode: PaymentMode;
    postSettlement?: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const MarkInvoicePaymentModal: React.FC<Props> = ({
    invoice, charge, mode, postSettlement, onClose, onSuccess,
}) => {
    const scenario: string = charge.chargeScenario || 'GUARANTEE';
    const allowedModes = PAYMENT_MODES_BY_SCENARIO[scenario] || ['TOTAL'];
    const liquidation = clampMode(mode, allowedModes);
    const ui = postSettlement ? POST_SETTLEMENT_UI : MODE_UI[liquidation];

    // Mesma referência do fluxo antigo: em Liquidar é o saldo do TÍTULO (liquidar é afirmação sobre
    // a DÍVIDA), nos outros modos é o da fatura, onde se paga parte daquela cobrança.
    const titleRemaining = Math.max(
        0, Number(charge.amount || 0) - Number(charge.paidAmount || 0));
    const invoiceRemaining = Number(invoice.amount || 0) - Number(invoice.amountPaid || 0);
    const expected = liquidation === 'TOTAL' ? titleRemaining : invoiceRemaining;

    const [payment, setPayment] = useState<PaymentRow | null>(null);
    const [infoPayment, setInfoPayment] = useState<PaymentRow | null>(null);
    const [notes, setNotes] = useState('');
    const [replaceAction, setReplaceAction] = useState<'CANCEL' | 'UNCOLLECT' | 'KEEP'>('CANCEL');
    const [submitting, setSubmitting] = useState(false);

    /**
     * O valor NÃO é digitado: ele É o do pagamento escolhido.
     *
     * Antes o campo vinha pré-preenchido com o esperado e era editável, o que convidava a inventar
     * um número — e a marcação não cria dinheiro, ela aponta dinheiro que já entrou. Digitar aqui
     * só poderia produzir uma marcação que não corresponde ao que o banco recebeu.
     *
     * Sem pagamento escolhido o campo fica VAZIO, com o valor da fatura no placeholder: assim o
     * usuário vê quanto precisa procurar na lista, sem que a tela finja já ter uma resposta.
     */
    const value = Number(payment?.amount || 0);
    const totalPayment = MODE_TO_TOTAL_PAYMENT[liquidation];

    /**
     * Desconto/acréscimo em relação ao esperado, como no fluxo antigo. Só em LIQUIDAÇÃO: numa
     * baixa parcial o valor é naturalmente menor que o esperado, sem ser desconto — chamar aquilo
     * de desconto ensinaria o usuário a ignorar o selo.
     */
    const valueHint = useMemo(
        () => (liquidation === 'TOTAL' && payment ? amountHint(value, expected) : null),
        [liquidation, payment, value, expected]);

    /**
     * Espelha a regra `use_original_invoice` do domínio. Serve para AVISAR: quando não dá para
     * reusar a fatura, o backend deriva uma nova — e é só nesse caso que existe "fatura anterior"
     * para a promessa agir.
     *
     * O critério é o que muda a COBRANÇA: valor diferente do faturado ou boleto envolvido. O modo
     * de liquidação NÃO entra — ele é instrução de liquidação, não termo de cobrança.
     */
    const reusesOriginal = useMemo(() => (
        value === Number(invoice.amount)
        && !invoice.boleto
        && payment?.paymentMethod !== 'BOLETO'
    ), [value, invoice.amount, invoice.boleto, payment]);

    /**
     * O que ainda sobra deste pagamento para marcar.
     *
     * ⚠️ PENDENTE não quer dizer "intocado": um pagamento de 1.000 com 600 já marcados em outro
     * título continua PENDENTE (a conta não fechou), e aparece na lista. Marcar os 1.000 dele aqui
     * seria recusado pelo servidor, que soma o que já está marcado. A guarda existe para esse
     * caso — e é o único, agora que o valor não é mais digitável.
     */
    const available = Math.max(0, (payment?.amount || 0) - (payment?.markedAmount || 0));
    const partiallyMarked = !!payment && (payment.markedAmount || 0) > 0;
    const exceedsPayment = !!payment && value > available;

    const podeEnviar = !!payment && value > 0 && !exceedsPayment && !submitting;

    const handleSubmit = async () => {
        if (!payment) return toast.error('Selecione o pagamento que paga esta fatura');
        if (!value || value <= 0) return toast.error('Informe um valor maior que zero');
        if (exceedsPayment) {
            return toast.error(
                `Este pagamento já tem parte marcada em outra fatura — sobram ${formatBRL(available)}`);
        }

        const item: MarkInvoiceItem = {
            invoice_id: invoice.invoiceId,
            value,
            // Sem fatura anterior não há alvo para a promessa de escopo único, e o servidor
            // recusa — a tela manda NOTHING, que é a forma de dizer "nada a fazer".
            payment_action_promise: reusesOriginal
                ? 'NOTHING' : PROMISE_BY_CHOICE[replaceAction],
            total_payment: totalPayment,
            notes: notes || undefined,
        };

        setSubmitting(true);
        try {
            await markInvoices(payment.paymentId, [item]);
            toast.success('Pagamento sinalizado. Aguardando aprovação do financeiro.');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível sinalizar: ${e.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">{ui.heading}</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {charge.contractLabel || charge.contractNumber} · fatura{' '}
                                {String(invoice.invoiceId).slice(0, 8)} · valor {formatBRL(invoice.amount)}
                                {invoice.dueDate && <> · vence {formatDateBR(invoice.dueDate)}</>}
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
                        {/* O que a ação faz, dito antes de tudo: quem vem do fluxo antigo espera
                            que este botão pague a fatura, e ele não paga. */}
                        <div className="px-3 py-2 text-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                            <p className="font-medium text-blue-900 dark:text-blue-200">
                                Isto não paga a fatura — sinaliza qual pagamento a paga.
                            </p>
                            <p className="text-blue-800 dark:text-blue-300 text-xs mt-0.5">
                                A fatura continua aberta até o financeiro aprovar a identificação, na
                                tela de <strong>Pagamentos</strong>. Só então ela é liquidada e o
                                título é movimentado.
                            </p>
                        </div>

                        <p className="text-sm px-3 py-2 bg-gray-100 dark:bg-slate-700 rounded">
                            Efeito no título quando aprovado:{' '}
                            <strong>{postSettlement
                                ? 'Recebimento a maior (título já liquidado)'
                                : MODE_LABELS[liquidation]}</strong>
                            {postSettlement && (
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    O título já está liquidado: este valor entra como recebimento a
                                    maior, sem reabrir a dívida.
                                </span>
                            )}
                            {!postSettlement && liquidation === 'OPERATION_WATERFALL' && (
                                <span className="block text-xs text-gray-500 mt-0.5">{WATERFALL_HINT}</span>
                            )}
                            {scenario === 'GUARANTEE' && (
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    Recebível de garantia: o efeito acima é no título de{' '}
                                    <strong>garantia</strong>. O valor é <strong>também</strong>{' '}
                                    distribuído pelas parcelas da operação, nos dois modos.
                                </span>
                            )}
                        </p>

                        <div>
                            <label className={LABEL}>Pagamento que paga esta fatura *</label>
                            <PendingPaymentPicker
                                selectedId={payment?.paymentId}
                                onSelect={setPayment}
                                onInfo={setInfoPayment}
                                exactAmount={Number(invoice.amount || 0)}
                            />
                        </div>

                        <div>
                            <label className={LABEL} htmlFor="valor-marcado">
                                Valor deste pagamento para esta fatura
                            </label>
                            {/* readOnly, não disabled: `disabled` tira o campo do fluxo de leitura
                                de leitor de tela, e este campo carrega informação — o valor que
                                será marcado. Cinza para o cadeado ser visível sem tooltip. */}
                            <input id="valor-marcado" className={`${INPUT} bg-gray-100 dark:bg-slate-800 cursor-not-allowed`}
                                readOnly
                                value={payment ? formatBRL(value) : ''}
                                placeholder={formatBRL(Number(invoice.amount || 0))}
                                title="O valor vem do pagamento escolhido — a marcação aponta dinheiro que já entrou, não cria um valor novo" />
                            {valueHint && <HintBadge hint={valueHint} />}
                            <p className="text-xs text-gray-500 mt-1">
                                {postSettlement
                                    ? <>Título já liquidado — o valor recebido não abate dívida</>
                                    : !payment
                                        ? <>Escolha o pagamento acima</>
                                        : null}
                            </p>
                            {partiallyMarked && (
                                <p className="text-xs text-red-600 mt-1">
                                    Este pagamento já tem {formatBRL(payment!.markedAmount || 0)}{' '}
                                    marcados em outra fatura — sobram apenas{' '}
                                    {formatBRL(available)}. Escolha outro pagamento, ou remova a
                                    sinalização daquele antes.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className={LABEL} htmlFor="obs-marcacao">Observações</label>
                            <input id="obs-marcacao" className={INPUT} value={notes}
                                placeholder="Referência bancária, NF, etc."
                                onChange={e => setNotes(e.target.value)} />
                            {/* acumula, não substitui — a fatura pode já ter observação de outra ação */}
                            <p className="text-xs text-gray-500 mt-1">
                                Somado às observações que a fatura já tiver.
                            </p>
                        </div>

                        {!reusesOriginal && (
                            <div className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                                <p className="font-medium text-amber-800 dark:text-amber-300">
                                    Esta marcação vai criar uma fatura nova
                                </p>
                                <p className="text-amber-700 dark:text-amber-400 mt-0.5">
                                    A fatura é imutável quanto à cobrança: valor diferente do faturado
                                    ou boleto envolvido exigem uma fatura nova. O que fazer com esta
                                    — <strong>quando o pagamento for aprovado</strong>:
                                </p>
                                <div className="flex gap-4 mt-2">
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="promessa" value="CANCEL"
                                            checked={replaceAction === 'CANCEL'}
                                            onChange={() => setReplaceAction('CANCEL')} />
                                        Cancelar
                                    </label>
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="promessa" value="UNCOLLECT"
                                            checked={replaceAction === 'UNCOLLECT'}
                                            onChange={() => setReplaceAction('UNCOLLECT')} />
                                        Só deixar de cobrar
                                    </label>
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="promessa" value="KEEP"
                                            checked={replaceAction === 'KEEP'}
                                            onChange={() => setReplaceAction('KEEP')} />
                                        Manter como está
                                    </label>
                                </div>
                                <p className="text-amber-700 dark:text-amber-400 mt-2">
                                    {replaceAction === 'KEEP'
                                        ? 'A fatura anterior continua em aberto e sendo cobrada, ao lado da nova.'
                                        : 'Nada acontece com ela agora: o efeito é aplicado na aprovação.'}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end gap-2">
                        <button onClick={onClose} disabled={submitting}
                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                            Cancelar
                        </button>
                        {/* âmbar, a cor de "aguardando alguém" nas telas de pagamento — e não o
                            verde de liquidado, que é o que esta ação justamente NÃO faz */}
                        <button onClick={handleSubmit} disabled={!podeEnviar}
                            className="px-4 py-2 text-sm text-white rounded disabled:opacity-60 bg-amber-500 hover:bg-amber-600">
                            {submitting ? ui.submittingLabel : 'Sinalizar Pagamento'}
                        </button>
                    </div>
                </div>
            </div>

            {/* z acima do próprio modal (que é z-[60]): sem isso o dialog de informações abria
                ATRÁS e o usuário só via o overlay escurecer, sem entender o que aconteceu */}
            {infoPayment && (
                <PaymentInfoModal payment={infoPayment} zIndexClass="z-[70]"
                                  onClose={() => setInfoPayment(null)} />
            )}
        </>
    );
};

export default MarkInvoicePaymentModal;
